import { computed, effect, inject, Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { AuthService } from '@cumlaude/shared-authentication';
import { RestService } from '@cumlaude/shared-services';
import {
	getRolDescription,
	InstellingBron,
	RCumLaudeAccount,
	RCumLaudeAccountAdditionalObjectKey,
	RCumLaudeModule,
	RRol,
} from '@cumlaude/service-contract';
import { intersection, isNil, isUndefined } from 'lodash-es';
import { Attr, AttrPath } from '@cumlaude/metadata';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { UserBugsnagService } from '@cumlaude/user-bugsnag';
import { blocklistIncludes, BlocklistType } from './blocklists';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ExtraFilterNames, FilterName } from './filter-name';

export const rolOrder: RRol[] = [RRol.SCHOOLBREED, RRol.TEAMLEIDER, RRol.SECTIELEIDER, RRol.DOCENT, RRol.MENTOR, RRol.AANWEZIGHEID, RRol.BESTUUR];

export function rollenForModules(bestuurEnabled: boolean, modules: RCumLaudeModule[]): RRol[] {
	let rollen = [RRol.SCHOOLBREED, RRol.TEAMLEIDER];

	if (modules.includes(RCumLaudeModule.SECTIE)) rollen.push(RRol.SECTIELEIDER);
	if (modules.includes(RCumLaudeModule.DOCENT)) rollen.push(RRol.DOCENT, RRol.MENTOR);
	if (modules.includes(RCumLaudeModule.SCHOOLAANWEZIGHEID)) rollen.push(RRol.AANWEZIGHEID);
	if (bestuurEnabled) rollen.push(RRol.BESTUUR);

	return rollen;
}

@Injectable({
	providedIn: 'root',
})
export class UserService {
	authService = inject(AuthService);
	restService = inject(RestService);

	myAccount$ = this.authService.loggedIn$.pipe(
		filter((x) => x),
		switchMap(() => this.restService.getMyAccount()),
		shareReplay(1)
	);

	account = toSignal(this.myAccount$);

	rollen$ = this.myAccount$.pipe(map(({ additionalObjects }) => additionalObjects![RCumLaudeAccountAdditionalObjectKey.AUTORISATIES].rollen));

	rollen = toSignal(this.rollen$);

	rolChanged$ = new ReplaySubject<RRol>(1);

	activeRol = toSignal(this.rolChanged$);

	instelling$ = this.myAccount$.pipe(
		switchMap((account) => this.restService.getInstelling(account.instelling)),
		shareReplay(1)
	);

	instelling = toSignal(this.instelling$);

	bron = computed(() => this.instelling()?.bron);

	begroetingsnaam = computed(() => {
		const account = this.account();
		if (!account) return '';
		return account.roepnaam || account.naam;
	});

	isSomtoday = computed(() => this.bron() === InstellingBron.Somtoday);

	isMagister = computed(() => this.bron() === InstellingBron.Magister);

	isBestuur = computed(() => this.activeRol() === RRol.BESTUUR);

	/** Lijst vestigingen uit de REST-service. Geeft alleen vestigingen waarvoor CumLaude actief is. Bevat ook de bijbehorende BRIN-nummers. */
	rVestigingen$ = this.rolChanged$.pipe(
		switchMap((rol) => this.restService.getVestigingen({ cumlaudeActief: rol !== RRol.BESTUUR, vanBestuur: rol === RRol.BESTUUR })),
		shareReplay(1)
	);

	vestigingen = toSignal(this.rVestigingen$, { initialValue: [] });

	beheerRVestigingen$ = this.myAccount$.pipe(
		filter((account) => account.rollen.includes(RRol.BEHEERDER)),
		switchMap(() => this.restService.getBeheerVestigingen())
	);

	modules = computed(() => {
		const vestigingen = this.vestigingen();
		return [...new Set(vestigingen.flatMap((vestiging) => vestiging.modules))];
	});

	modules$ = toObservable(this.modules);

	vo = computed(() => {
		const vestigingen = this.vestigingen();
		return vestigingen.some((vestiging) => vestiging.vo);
	});

	vso = computed(() => {
		const vestigingen = this.vestigingen();
		return vestigingen.some((vestiging) => vestiging.vso);
	});

	schoolaanwezigheidVestigingen = computed(() => {
		const vestigingen = this.vestigingen();
		return vestigingen
			.filter((vestiging) => vestiging.modules.includes(RCumLaudeModule.SCHOOLAANWEZIGHEID))
			.map((vestiging) => vestiging.vestigingId);
	});

	useBestuurBlocklist = computed(() => this.activeRol() === RRol.BESTUUR);

	useMagisterBlockList = computed(() => this.isMagister());

	useSomtodayBlockList = computed(() => this.isSomtoday());

	useNoVSOBlockList = computed(() => !this.vso());

	useNoVOBlockList = computed(() => !this.vo());

	constructor() {
		const bugsnag = inject(UserBugsnagService);

		effect(() => {
			let rollen = this.rollen();
			if (isUndefined(rollen)) return;

			const activeRol = this.getActiveRol();
			if (isUndefined(activeRol) || !rollen.includes(activeRol)) this.changeRol(intersection(rolOrder, rollen)[0]);
		});
		effect(() => {
			const rol = this.activeRol();
			if (rol) bugsnag.updateRol(rol);
		});
		effect(() => {
			const account = this.account();
			if (account) bugsnag.updateAccount(account);
		});
		effect(() => {
			const instelling = this.instelling();
			if (instelling) bugsnag.updateInstelling(instelling);
		});

		this.initActiveRol();
	}

	/**
	 * Wijzigt de rol in de session storage en brengt dan alle onderdelen die er van afhankelijk zijn op de hoogte dat de rol gewijzigd is.
	 */
	changeRol(rol: RRol) {
		if (isUndefined(rol)) sessionStorage.removeItem('rol');
		else sessionStorage.setItem('rol', rol);

		this.rolChanged$.next(rol);
	}

	isAttAllowed(name: FilterName) {
		return name.split('.').every((attr) => this.isAttrAllowed(<Attr | ExtraFilterNames>attr));
	}

	isAttrPathAllowed(attrPath: AttrPath) {
		return attrPath.every((attr) => this.isAttrAllowed(attr));
	}

	isAttrAllowed(attr: Attr | ExtraFilterNames) {
		const bestuur = !this.useBestuurBlocklist() || !blocklistIncludes(BlocklistType.BESTUUR, attr);
		const magister = !this.useMagisterBlockList() || !blocklistIncludes(BlocklistType.MAGISTER, attr);
		const somtoday = !this.useSomtodayBlockList() || !blocklistIncludes(BlocklistType.SOMTODAY, attr);
		const noVSO = !this.useNoVSOBlockList() || !blocklistIncludes(BlocklistType.GEEN_VSO, attr);
		const noVO = !this.useNoVOBlockList() || !blocklistIncludes(BlocklistType.GEEN_VO, attr);
		return bestuur && magister && somtoday && noVO && noVSO;
	}

	getAccountRolDescription(rol: RRol | undefined, account: RCumLaudeAccount): string {
		const rolDescription = getRolDescription(rol);

		let autorisaties: string[] = [];
		const autorisatieData = account.additionalObjects![RCumLaudeAccountAdditionalObjectKey.AUTORISATIES];
		switch (rol) {
			case RRol.TEAMLEIDER:
				autorisaties = autorisatieData.teamleider;
				break;
			case RRol.SECTIELEIDER:
				autorisaties = autorisatieData.sectieleider;
				break;
			case RRol.DOCENT:
				autorisaties = autorisatieData.docent;
				break;
			case RRol.MENTOR:
				autorisaties = autorisatieData.mentor;
				break;
		}

		if (autorisaties.length > 0) return `${rolDescription}: ${autorisaties.join(', ')}`;
		return rolDescription;
	}

	/**
	 * Initialiseert de diverse onderdelen wat de rol is die uit de session storage komt.
	 */
	private initActiveRol() {
		const activeRol = this.getActiveRol();
		if (activeRol) this.changeRol(activeRol);
	}

	/**
	 * Geeft de actieve rol terug uit de session storage.
	 */
	getActiveRol(): RRol | undefined {
		const rol = sessionStorage.getItem('rol');
		if (!isNil(rol)) return <RRol>rol;
		return undefined;
	}
}
